import TableView, {
  ColumnConfig,
} from "../../../components/controls/table-view";
import Button from "../../../components/controls/button";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { showSuccess } from "../../../components/notification/toastr-actions";
import { useAppSelector } from "../../../hooks/useAppSelector";
import {
  resetProductState,
  bulkUploadProducts,
  updateProductField,
} from "../../../store/features/supplier/supplier-product-upload-slice";
import {
  ProductContainer,
  ProductNameContainer,
  ProductTitle,
} from "../../../styles/shared/product";
import { ICON_ID } from "../../../components/icons/google-icon";
import { currencyFormatter } from "../../../helpers/formatters";
import styled from "styled-components";
import { ModalID } from "../../../constants/modal-constants";
import { openModal } from "../../../store/features/modal/modal-slice";
import { removeProductByName } from "../../../store/features/supplier/supplier-product-upload-slice";
import EditableField from "../../../components/controls/editable-field";
import { ApplicationSizeUnit } from "../../../models/product/application-size-unit";
import { ApplicationSupplierProductsList } from "../../../models/supplier/application-supplier-product-list";

interface UploadSupplierProductProps {
  supplierId: number;
  handleUploadSuccess: (data: ApplicationSupplierProductsList) => void;
}

export default function UploadSupplierProducts({
  supplierId,
  handleUploadSuccess,
}: UploadSupplierProductProps) {
  const products = useAppSelector((state) => state.supplierProductUpload);
  const dispatch = useAppDispatch();
  const renderActionButton = (row: any) => (
    <Button
      variant="outlined"
      id={row.name}
      label="Remove"
      isDanger={true}
      fullWidth={false}
      onClick={() => handleRemoveProduct(row.name)}
    />
  );

  const handleUploadProducts = async () => {
    const response = await dispatch(
      bulkUploadProducts({
        supplierId: supplierId,
        products: products?.products,
      })
    );
    if (response) {
      const data = response.payload as ApplicationSupplierProductsList
      dispatch(showSuccess(`Products uploaded successfully!`));
      handleUploadSuccess(data);
      dispatch(resetProductState());
    }
  };

  const handleRemoveProduct = (productName: string) => {
    dispatch(removeProductByName(productName));
  };


  const columnConfig: { [key: string]: ColumnConfig } = {
    id: {
      hidden: true
    },
    openPricing: {
      hidden: true
    },
    information: {
      hidden: true
    },
    name: {
      name: "Product",
      order: 1,

      render: (value: string, row: any) => (
        <ProductContainer>
          <ProductNameContainer>
            <ProductTitle>{value}</ProductTitle>
            <div>{row.size} {row.sizeunit}</div>
          </ProductNameContainer>
        </ProductContainer>
      ),
    },
    reference: {
      order: 2,
      render: (value: string | null, row: any) => {
        return (
          <EditableField
            type="text"
            value={row.reference}
            label="Reference"
            displayValue={value ? `${value}` : "Not set"}
            onSave={(savedValue) =>
              dispatch(updateProductField({
                name: row.name,
                field: "reference",
                value: savedValue
              }))
            }
          />
        );
      },
    },
    minimumOrderQuantity: {
      name: "Min Qty"
    },
    plu: {
      order: 3,
      render: (value: string | null, row: any) => {
        return (
          <EditableField
            type="text"
            value={row.plu}
            label="Plu"
            displayValue={value ? `${value}` : "Not set"}
            onSave={(savedValue) =>
              dispatch(updateProductField({
                name: row.name,
                field: "plu",
                value: savedValue
              }))
            }
          />
        );
      },
    },
    origin: {
      order: 4,
      render: (value: string | null, row: any) => {
        return (
          <EditableField
            type="text"
            value={row.origin}
            label="Origin"
            displayValue={value ? `${value}` : "Not set"}
            onSave={(savedValue) =>
              dispatch(updateProductField({
                name: row.name,
                field: "origin",
                value: savedValue
              }))
            }
          />
        );
      },
    },
    price: {
      order: 5,
      render: (value: number | null, row: any) => {
        return (
          <>
            {row.openPricing == true ? "TBC" :
              <EditableField
                type="currency"
                value={row.Price}
                displayValue={value ? currencyFormatter.format(value) : "Not set"}
                label="Price"
                onSave={(savedValue) =>
                  dispatch(updateProductField({
                    name: row.name,
                    field: "price",
                    value: savedValue
                  }))
                }
              />
            }
          </>
        );
      },
    },

    size: {
      order: 8,
      render: (value: number | null, row: any) => {
        return (
          <EditableField
            type="text"
            value={row.size}
            displayValue={value ? `${value}` : "Not set"}
            label="Size"
            onSave={(savedValue) =>
              dispatch(updateProductField({
                name: row.name,
                field: "size",
                value: savedValue
              }))
            }
          />
        );
      },
    },
    sizeunit: {
      name: "Unit",
      order: 9,
      render: (value: number | null, row: any) => {
        return (
          <EditableField
            type="dropdown"
            value={row.sizeunit}
            options={Object.values(ApplicationSizeUnit).map((x) => ({ value: x, label: x }))}
            displayValue={value ? `${value}` : "Not set"}
            label="Unit"
            onSave={(savedValue) =>
              dispatch(updateProductField({
                name: row.name,
                field: "sizeunit",
                value: savedValue
              }))
            }
          />
        )
      },
    },
  };

  return (
    <>


      {products.products?.length > 0 ? (
        <ButtonContainer>
          <Button
            variant="outlined"
            label="Cancel"
            icon={ICON_ID.Reset}
            id="btnUploadProducts"
            onClick={() => dispatch(resetProductState())}
          />
          <Button
            variant="contained"
            label="Save Products"
            icon={ICON_ID.Save}
            id="btnUploadProducts"
            onClick={handleUploadProducts}
          />
        </ ButtonContainer>

      ) : (
        <Button
          variant="contained"
          icon={ICON_ID.Upload}
          label="Upload Products by CSV"
          id="btnUploadProducts"
          onClick={() =>
            dispatch(
              openModal({
                id: ModalID.UploadSupplierProduct,
                data: { id: supplierId },
              })
            )
          }
        />
      )}

      <TableContainer>

        <TableView
          id="tblUploadSupplieProducts"
          emptyText="Upload Products by CSV"
          totalCount={products?.products?.length ?? 0}
          data={products.products ?? []}
          columnConfig={columnConfig}
          actionButton={renderActionButton}
          showHeaders={true}
        />
      </TableContainer>

    </>
  );
}

const TableContainer = styled.div`

`;

const ButtonContainer = styled.div`
  display: flex;
  gap:10px;
`;
